import { Modal } from '@/components/common';
import { User } from '@/model/AccountModel';
import { Button, Form, Input, Spin } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import { batch } from 'react-redux';
import { AddPersonGroupModalData, ErrorType, PersonGroupModelWithKey } from './PersonGroupsModal';
import styles from './personGroupsModal.module.scss';

type Props = {
	data: AddPersonGroupModalData;
	savePersonGroup: (personGroup: PersonGroupModelWithKey) => void;
	setAddData: (data: AddPersonGroupModalData) => void;
	setShowModal: (flag: boolean) => void;
	loading: boolean;
	setLoading: (load: boolean) => void;
};

const user: User = getUser();
const AddOrEditPersonGroupModal: FC<Props> = ({ data, savePersonGroup, setAddData, setShowModal, loading, setLoading }) => {
	// #region state management variables
	const [name, setName] = useState<string>('');
	const [localError, setLocalError] = useState<boolean>(false);
	const [localErrorMessage, setLocalErrorMeessage] = useState<string>('');
	const { isFolder, personGroup, error, errorMessage } = data;
	// #endregion

	const inputRef = useRef(null);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	useEffect(() => {
		setLocalError(error === ErrorType.Name);
		setLocalErrorMeessage(errorMessage);
		if (loading) {
			setLoading(false);
		}
	}, [error, errorMessage]);

	const isEdit = useMemo<boolean>(() => {
		let isEdit: boolean = false;
		if (personGroup) {
			const { PersonGroupId, PersonFolderId } = personGroup;
			isEdit = (isFolder && PersonFolderId > 0) || (!isFolder && PersonGroupId > 0);
			setName(isFolder ? personGroup.FolderName : personGroup.GroupName);
		}
		return isEdit;
	}, [personGroup]);

	const modalTitle = useMemo(() => {
		let title: string = '';
		if (isEdit) {
			title = `${_('Edit')} ${_(isFolder ? 'SubFolder' : 'PersonGroup')}`;
		} else {
			if (isFolder) {
				title = `${_('Add')} ${_('SubFolder')}`;
			} else {
				title = `Add New ${_('PersonGroup')}`;
			}
		}
		return title;
	}, [isEdit]);

	const onSave = () => {
		const newName = name.trim();
		if (newName) {
			let newPersonGroup: PersonGroupModelWithKey = {
				FolderName: null,
				GroupName: null,
				ParentFolderId: 0,
				PersonFolderId: 0,
				PersonGroupId: 0,
				parentKey: personGroup?.parentKey,
			};
			if (personGroup) {
				if (isEdit) {
					newPersonGroup = {
						...personGroup,
					};
				} else {
					newPersonGroup.PersonGroupId = personGroup.PersonGroupId;
					if (isFolder) {
						newPersonGroup.ParentFolderId = personGroup.ParentFolderId;
					}
				}
			}
			if (isFolder) {
				newPersonGroup.FolderName = newName;
			} else {
				newPersonGroup.GroupName = newName;
			}
			savePersonGroup(newPersonGroup);
			setLoading(true);
		} else {
			batch(() => {
				setLocalError(true);
				setLocalErrorMeessage(_('NameIsRequired'));
			});
		}
	};

	const onCancel = () => {
		setShowModal(false);
	};

	const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setName(value.substring(0, 32));
		batch(() => {
			if (error !== ErrorType.None) {
				setAddData({ ...data, error: ErrorType.None, errorMessage: '' });
			}
			setLocalError(false);
			setLocalErrorMeessage('');
		});
	};

	return (
		<Modal
			footer={[
				<Button id='addPersonGroupSaveButton' key='save' onClick={onSave} type='primary'>
					{_('SaveChanges')}
				</Button>,
				<Button id='addPersonGroupCancelButton' key='cancel' onClick={onCancel}>
					{_('Cancel')}
				</Button>,
			]}
			visible
			title={modalTitle}
			onCancel={onCancel}
			customZoomClass={styles.addModal}
			width={450}>
			<Spin spinning={loading} tip={_('loading')}>
				<Form.Item label={_('Name')} htmlFor='name'>
					<Input ref={inputRef} id='name' className={cx({ [styles.nameErrorInput]: localError })} onChange={onChangeName} value={name} />
					{localError && (
						<div>
							<div className='ant-form-item-explain ant-form-item-explain-connected'>
								<div className='ant-form-item-explain-error'>{localErrorMessage}</div>
							</div>
						</div>
					)}
				</Form.Item>
			</Spin>
		</Modal>
	);
};

export { AddOrEditPersonGroupModal };
